import { useTranslation } from 'react-i18next';
import { CollapseText } from '@components/collapse-text';
import { EventSummaryCard } from '@components/event-summary-card';
import { IconPerson, IconTicket } from '@components/icons';
import { AdmissionType } from '@domain/admission';
import { formatOfflinePrice, totalOnlinePrice } from '@utils/calculate-booking-price';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { HeaderEventFormSummaryProps } from './header-event-form-summary.type';

export const HeaderEventFormSummary = ({
  title,
  offer,
  quantity,
  selectedAddons,
  addons,
  event,
}: HeaderEventFormSummaryProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const offerName =
    getTranslatedPropertyBasedOnOrder({
      translations: offer?.name,
      preferredLanguage: locale,
    }) ?? t(getDefaultOfferNameTranslation(offer.admissionType as AdmissionType));

  const offerDescription =
    getTranslatedPropertyBasedOnOrder({
      translations: offer?.description,
      preferredLanguage: locale,
    }) ?? undefined;

  return (
    <header className='flex flex-col px-4 sm:px-20 pt-8 text-left'>
      <div className='flex flex-col gap-2 mb-4'>
        <p className='text-tertiary text-body-s'>{title}</p>
        <h2 className='text-headline-m line-clamp-2 text-primary'>{offerName}</h2>
        {offerDescription && (
          <CollapseText text={offerDescription} expandText={t('common:viewMore')} collapseText={t('common:viewLess')} />
        )}
        <p className='flex text-body-s gap-2 text-secondary'>
          <span className='flex gap-1'>
            <IconPerson className='w-4 h-4' /> {t('invitations:guests', { count: quantity })}
          </span>
          <span className='flex gap-1'>
            <IconTicket className='w-4 h-4' />
            {t('offers:totalPriceValue', {
              price: totalOnlinePrice({ selectedAddons, quantity, addons, locale: i18n.language, offer }),
            })}
            {!!offer?.price?.offlinePrice &&
              t('offers:offlinePriceValue', {
                price: formatOfflinePrice({ offer, locale: i18n.language, quantity }),
              })}
          </span>
        </p>
      </div>
      <EventSummaryCard
        name={event.name}
        startingTime={event.startingTime}
        coverUrl={event.coverUrl}
        clubName={event.club.name}
        locale={i18n.language}
        timezone={event.club.city.timezone}
      />
    </header>
  );
};
